import React from 'react';
import { Helmet } from 'react-helmet';
import SEOMetadata from '@/components/SEOMetadata';
import HeroSection from '@/components/HeroSection';
import CTASection from '@/components/CTASection';
import CoachingNuernbergFAQ from '@/components/coaching-nuernberg/CoachingNuernbergFAQ';
import LocalSEOStructuredData from '@/components/seo/LocalSEOStructuredData';
import { webPageStructuredData } from '@/data/structuredData';
import { MapPin, TreePine, Users, ArrowRight, Clock, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';

const localBusinessData = {
  name: "Jörg Hörmann - Executive Coaching & Finanzcoaching",
  description: "Executive Coaching und Finanzcoaching für Nürnberg, Roth und die Metropolregion. Outdoor-Coaching am Rothsee als Premium-Alternative.",
  additionalType: "https://schema.org/ServiceAreaBusiness",
  address: {
    city: "Roth",
    postalCode: "91154",
    region: "Bayern",
    country: "DE"
  },
  geo: {
    latitude: 49.2401,
    longitude: 11.0967
  },
  phone: "+49-9171-9892118",
  email: "info@joerg-hoermann.de",
  url: "https://joerg-hoermann.de",
  serviceArea: [
    "Nürnberg", "Roth", "Schwabach", "Erlangen", "Ingolstadt",
    "Fürth", "Hilpoltstein", "Georgensgmünd", "Metropolregion Nürnberg"
  ] as string[],
  openingHours: ["Mo-Fr 09:00-18:00"] as string[],
  priceRange: "€€€",
  paymentAccepted: ["Cash", "Invoice", "Bank Transfer"] as string[]
};

const localServices = [
  {
    name: "Executive Coaching Nürnberg",
    description: "Strategisches Executive Coaching für Führungskräfte in der Metropolregion Nürnberg. Vor Ort, remote oder als Walk & Talk am Rothsee.",
    url: "https://joerg-hoermann.de/coaching-nuernberg",
    areaServed: ["Nürnberg", "Roth", "Schwabach", "Erlangen", "Ingolstadt"] as string[]
  },
  {
    name: "Finanzcoaching Region Nürnberg",
    description: "Individuelles Finanzcoaching für Privatpersonen in Nürnberg und Umgebung. Geldblockaden lösen und finanzielle Klarheit gewinnen.",
    url: "https://joerg-hoermann.de/finanzcoaching",
    areaServed: ["Nürnberg", "Roth", "Schwabach", "Erlangen"] as string[]
  },
  {
    name: "Outdoor-Coaching am Rothsee",
    description: "Walk & Talk Coaching am Rothsee im Fränkischen Seenland – nur 30 Minuten von Nürnberg. Premium-Coaching in der Natur.",
    url: "https://joerg-hoermann.de/outdoor-coaching",
    areaServed: ["Rothsee", "Fränkisches Seenland", "Nürnberg", "Roth"] as string[]
  }
];

const CoachingNuernberg: React.FC = () => {
  return (
    <>
      <SEOMetadata
        metaTitle="Coaching Nürnberg & Roth | Executive & Finanzcoaching"
        metaDescription="Executive & Finanzcoaching in Nürnberg, Roth & Umgebung. Walk & Talk am Rothsee als Premium-Alternative. ✆ Kostenloses Erstgespräch vereinbaren!"
        title="Coaching Nürnberg & Roth | Executive & Finanzcoaching"
        description="Executive Coaching und Finanzcoaching für Nürnberg, Roth und die Metropolregion. Outdoor-Coaching am Rothsee als Premium-Alternative zum Büro-Coaching."
        pageName="Coaching Nürnberg"
        primaryLink="/coaching-nuernberg"
        canonicalUrl="https://joerg-hoermann.de/coaching-nuernberg"
        keywords="Coaching Nürnberg, Executive Coaching Nürnberg, Finanzcoaching Nürnberg, Coach Roth, Outdoor-Coaching Rothsee, Walk Talk Nürnberg, Business Coach Metropolregion Nürnberg, Coaching Schwabach, Führungskräfte-Coaching Erlangen"
      />
      
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(webPageStructuredData({
            name: "Executive & Finanzcoaching für Nürnberg & Roth",
            description: "Coaching-Angebote für die Metropolregion Nürnberg: Executive Coaching, Finanzcoaching und Walk & Talk am Rothsee.",
            url: "https://joerg-hoermann.de/coaching-nuernberg",
            aboutTopics: ["executiveCoaching", "finanzcoaching", "outdoorCoaching"]
          }))}
        </script>
      </Helmet>

      <LocalSEOStructuredData
        businessData={localBusinessData}
        services={localServices}
      />

      {/* AI-Summary / AEO Abstract */}
      <section className="sr-only" aria-label="Zusammenfassung" data-speakable="summary">
        <p>Jörg Hörmann bietet Executive Coaching und Finanzcoaching für Klienten aus Nürnberg, Roth, Schwabach und der gesamten Metropolregion. Sein Signature-Format „Walk &amp; Talk" am Rothsee im Fränkischen Seenland bietet eine naturnahe Premium-Alternative zum klassischen Büro-Coaching – nur 30 Minuten von Nürnberg entfernt.</p>
      </section>

      {/* Hero Section */}
      <HeroSection
        title="Executive & Finanzcoaching für Nürnberg & Roth"
        subtitle="Ihr Coach in der Metropolregion"
        description="Coaching, das wirkt – vor Ort in Nürnberg, remote oder als Walk & Talk am Rothsee. Bequem erreichbar für Klienten aus Nürnberg, Schwabach, Erlangen und dem gesamten Landkreis Roth."
        backgroundClass="bg-joerg-50"
        imageUrl="/images/coaching-nuernberg-hero.jpg"
        hideHeroLogo={true}
      />

      {/* Coaching-Formate Section */}
      <section className="section-spacing bg-background overflow-x-hidden">
        <div className="container max-w-6xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block text-primary font-medium mb-4 px-3 py-1 bg-primary/10 rounded-full text-sm animate-fade-in">
              Ihre Optionen
            </span>
            <h2 className="animate-fade-in" style={{ animationDelay: '100ms' }}>
              Drei Wege zum Coaching in der Region
            </h2>
            <p className="text-muted-foreground text-lg mt-4 animate-fade-in" style={{ animationDelay: '200ms' }}>
              Ob Executive Coaching, Finanzcoaching oder Outdoor-Erlebnis – wählen Sie das Format, das zu Ihnen passt.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {/* Executive Coaching */}
            <div className="bg-card rounded-2xl p-6 sm:p-8 border border-border hover:shadow-lg transition-shadow animate-fade-in" style={{ animationDelay: '100ms' }}>
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-foreground">Executive Coaching</h3>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Strategische Begleitung für Führungskräfte. Katalysator-Führung, emotionale Intelligenz und nachhaltige Entwicklung.
              </p>
              <div className="flex items-center text-sm text-muted-foreground mb-4">
                <Clock className="w-4 h-4 mr-2" />
                <span>Ab 60 Min.</span>
              </div>
              <a href="/executive-coaching" className="inline-flex items-center text-primary font-medium hover:underline">
                Mehr erfahren <ArrowRight className="w-4 h-4 ml-1" />
              </a>
            </div>

            {/* Finanzcoaching */}
            <div className="bg-card rounded-2xl p-6 sm:p-8 border border-border hover:shadow-lg transition-shadow animate-fade-in" style={{ animationDelay: '200ms' }}>
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                <Star className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-foreground">Finanzcoaching</h3>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Für Privatpersonen: Geldblockaden lösen, finanzielle Klarheit gewinnen und eine gesunde Beziehung zu Geld entwickeln.
              </p>
              <div className="flex items-center text-sm text-muted-foreground mb-4">
                <Clock className="w-4 h-4 mr-2" />
                <span>Ab 60 Min.</span>
              </div>
              <a href="/finanzcoaching" className="inline-flex items-center text-primary font-medium hover:underline">
                Mehr erfahren <ArrowRight className="w-4 h-4 ml-1" />
              </a>
            </div>

            {/* Outdoor-Coaching */}
            <div className="bg-card rounded-2xl p-6 sm:p-8 border border-border hover:shadow-lg transition-shadow animate-fade-in" style={{ animationDelay: '300ms' }}>
              <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center mb-6">
                <TreePine className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-foreground">Walk & Talk am Rothsee</h3>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Premium-Coaching in der Natur. Nur 30 Min. von Nürnberg – wissenschaftlich belegt wirksamer als klassisches Büro-Coaching.
              </p>
              <div className="flex items-center text-sm text-muted-foreground mb-4">
                <Clock className="w-4 h-4 mr-2" />
                <span>Ab 90 Min.</span>
              </div>
              <a href="/outdoor-coaching" className="inline-flex items-center text-primary font-medium hover:underline">
                Mehr erfahren <ArrowRight className="w-4 h-4 ml-1" />
              </a>
            </div>
          </div>

          <div className="text-center mt-10 animate-fade-in" style={{ animationDelay: '400ms' }}>
            <a href="/preise" className="inline-flex items-center text-primary font-medium hover:underline text-lg">
              Alle Preise & Pakete im Überblick <ArrowRight className="w-4 h-4 ml-2" />
            </a>
          </div>
        </div>
      </section>

      {/* Walk & Talk am Rothsee – Premium Section */}
      <section className="section-spacing bg-muted/30 overflow-x-hidden">
        <div className="container max-w-5xl">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="animate-fade-in">
              <span className="inline-block text-primary font-medium mb-4 px-3 py-1 bg-primary/10 rounded-full text-sm">
                Das Premium-Format
              </span>
              <h2 className="mb-6 break-words hyphens-auto" lang="de">
                Walk &amp; Talk am Rothsee – Ihre Alternative zum Büro-Coaching
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                Statt Konferenzraum in Nürnberg: Coaching in Bewegung am Rothsee im Fränkischen Seenland. 
                Neurowissenschaftliche Studien belegen, dass Gehen die kognitive Flexibilität um bis zu 60 % steigert 
                und kreatives Denken fördert.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "Nur 30 Autominuten von Nürnberg (A6/B2)",
                  "Bewegung aktiviert neue Denkprozesse",
                  "Natur reduziert Stress und öffnet Perspektiven",
                  "Vertraulich und ohne Büro-Ablenkungen",
                  "Auch für Klienten aus Schwabach, Erlangen & Ingolstadt"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-foreground">
                    <MapPin className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <a
                href="https://zeeg.me/joerghoermann"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button size="lg" className="group">
                  Erstgespräch in der Region vereinbaren
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
            </div>
            <div className="relative animate-fade-in" style={{ animationDelay: '200ms' }}>
              <div className="bg-gradient-to-br from-primary/5 to-accent/10 rounded-2xl p-6 sm:p-8 border border-border overflow-hidden">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                      <MapPin className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground mb-1">Standort</h4>
                      <p className="text-muted-foreground text-sm">Rothsee, Fränkisches Seenland – zwischen Nürnberg und Roth</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                      <Clock className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground mb-1">Anfahrt</h4>
                      <p className="text-muted-foreground text-sm">
                        Nürnberg: ~30 Min. | Schwabach: ~20 Min. | Erlangen: ~40 Min. | Ingolstadt: ~60 Min.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                      <TreePine className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground mb-1">Setting</h4>
                      <p className="text-muted-foreground text-sm">Walk & Talk entlang des Rothsee-Rundwegs – wetterfest und ganzjährig möglich</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <CoachingNuernbergFAQ />

      {/* CTA Section */}
      <CTASection
        title="Kostenloses Erstgespräch in der Region vereinbaren"
        description="Ob Nürnberg, Roth oder Walk & Talk am Rothsee – im unverbindlichen Erstgespräch finden wir das passende Format für Sie."
        primaryCTA={{
          text: "Termin vereinbaren",
          link: "https://zeeg.me/joerghoermann",
          isExternal: true
        }}
        secondaryCTA={{
          text: "Kontakt aufnehmen",
          link: "/kontakt"
        }}
        backgroundClass="bg-gradient-to-br from-[#EDF5E9] to-[#E8F1FB] text-joerg-800"
      />
    </>
  );
};

export default CoachingNuernberg;
